// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import Scrollbars from 'react-custom-scrollbars';
import { toast } from 'react-toastify';

import Navigation from '../Navigation'
import MatchCounter from '../MatchCounter';
import Search from '../Search';
import User from '../User';

import api from '../../api';

import '../../styles/timeline.css';

const getFields = (m, key) => m?.filter(f => f.key === key) ?? [];
const getField = (m, key) => getFields(m, key)[0] ?? null;
const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default class Timeline extends React.Component {

    state = {
        name: null,
        events: [],
        years: [],
        matches: [],
        matchIdx: -1,
        query: "",
        searching: false,
        lines: 1,
        numDocResults: 0,
        sortDir: -1,
    }

    sortTimeline = async (sortDirection) => {
        // Estimate relative line numbers of anchors
        let lines = 0;

        const estimateLines = (m) => {
            let l = 0;
            const summary = getField(m, 'summary');
            if (summary) l += 2;

            l += getFields(m, 'medication').length ?? 1;
            
            const cpt = getFields(m, 'cpt');
            if (cpt) l += 2 + cpt.length;

            const icd = getFields(m, 'icd');
            if (icd) l += 2 + icd.length;
            
            return l;
        }

        const events = this.state.events.map(e => ({
            ...e,
            metadata: e.metadata?.map((f,i) => ({ ...f, idx: i })),
            date: new Date(getField(e.metadata, 'event date')?.value),
        })).filter(e => e.date).sort((a,b) => a.date < b.date ? sortDirection : sortDirection *-1);

        for (const [i,e] of events.entries()) {
            e.line = lines;
            if (i < events.length - 1)
                lines += estimateLines(e.metadata);
        }

        const years = [];
        // Compile year anchor points for the scrollbar
        for (const [idx, e] of events.entries()) {
            const year = e.date.getFullYear();
            const last = years.length > 0 ? years[years.length-1] : null;
            if (last?.year === year)
                continue;
            years.push({
                year,
                anchor: e.document,
                idx
            });
        }

        await this.setState({
            events,
            lines,
            years,
            matches: [],
            matchIdx: -1,
            query: "",
            searching: false
        });
        this.props.loaded();
    }


    loadTimeline = async (stack) => {
        // Load timeline information from all documents in stack
        this.props.loading();
        const res = await api.get('/stack/timeline?stackId=' + stack);
        if (!res.success) return toast.error("Unable to load medical events timeline");

        const name = res.name ?? "";
        const events = res.events ?? [];
        
        this.setState({
            name,
            events
        });

        await this.sortTimeline(this.state.sortDir);
    }


    componentDidMount = async () => {
        this.props.loading();
        if (!this.props.match.params.stackId) return;
        this.loadTimeline(this.props.match.params.stackId);
    }

    componentDidUpdate = async (props) => {
        if (props.match.params.stackId !== this.props.match.params.stackId)
            this.loadTimeline(this.props.match.params.stackId);
    }

    search = (query) => {
        if (query.length < 3)
            return this.setState({ query, matches: [], searching: false, matchIdx: -1, numDocResults: 0 });

        this.setState({ query, matches: [], searching: true });
        
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = undefined;
        }

        this.searchTimeout = setTimeout(() => {
            // Get number of results in documents in parallel
            (async () => {
                const res = await api.get('/document/search?searchLimit=0&stackId=' + this.props.match.params.stackId + '&searchQuery=' + encodeURIComponent(query));
                if (!res?.success) return;
                this.setState({ numDocResults: res.count });
            })();

            // Look for matches in events
            const matches = [];
            for (const [eventIdx, e] of this.state.events.entries()) {
                // Is there a match in this event?
                for (const f of e.metadata) {
                    if (![ 'report date', 'report type', 'title', 'summary', 'medication', 'cpt', 'icd', 'physician', 'facility' ].includes(f.key)) continue;
                    const value = f.value.toLowerCase();
                    // Find all indices of the query in the field value
                    let start = 0;
                    while (start < value.length) {
                        const idx = value.indexOf(query, start);
                        if (idx < 0) break;
                        matches.push({
                            eventIdx,
                            fieldIdx: f.idx,
                            key: f.key,
                            start: idx,
                            end: idx + query.length
                        });
                        start = idx + 1;
                    }
                }
            }
            this.setState({
                searching: false,
                matches: matches.map((m,i) => ({ ...m, matchIdx: i })),
                matchIdx: matches.length > 0 ? 0 : -1
            });
            this.searchTimeout = undefined;
        }, 500);
    }

    gotoMatch = (i) => {
        this.setState({ matchIdx: i });
        // Scroll to the match
        // Get the match div
        const mdiv = document.getElementById("timeline-match-" + String(i));
        if (!mdiv) return;
        // Scroll to the div
        mdiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    alert = (f) => {
        if (!f?.confidence || f.confidence >= 0.9) return "";
        if (f.confidence < 0.6) return <span className="timeline-confidence-low" />;
        return <span className="timeline-confidence-medium" />;
    }

    year = (y,i) => {
        // // Figure out what % of the scrollbar this year is in
        // // Get top of the timeline-event element
        // const anchor = document.getElementById("timeline-event-" + String(y.idx));
        // if (!anchor) return (<React.Fragment key={i} />);

        // // Get height of the timeline container
        // const container = document.getElementById("timeline-container");
        // if (!container) return (<React.Fragment key={i} />);

        // // Relative position
        // const top = String(Math.round(100 * anchor.getBoundingClientRect().top / container.getBoundingClientRect().height, 2)) + "%";

        // Get index of the year's anchor document
        const d = this.state.events.find(e => e.document === y.anchor);
        const top = String(Math.round(100 * d.line / this.state.lines)) + "%";
        return (<div
            className="timeline-year-anchor"
            key={i}
            style={{ top }}
        >{y.year}</div>);
    }

    match = (m,i) => {
        // // Figure out what % of the scrollbar this match is in
        // // Get top of the timeline-match element
        // const anchor = document.getElementById("timeline-match-" + String(m.matchIdx));
        // if (!anchor) return (<React.Fragment key={i} />);

        // // Get height of the timeline container
        // const container = document.getElementById("timeline-container");
        // if (!container) return (<React.Fragment key={i} />);

        // // Relative position
        // const top = String(Math.round(100 * anchor.getBoundingClientRect().top / container.getBoundingClientRect().height, 2)) + "%";

        // Get the event index of the match
        const e = this.state.events[m.eventIdx] ?? { line: 0 };
        const top = String(Math.round(100 * e.line / this.state.lines)) + "%";
        return (<div
            className="timeline-match-anchor"
            key={i}
            style={{ top }}
        />);
    }

    highlight = (ei, f) => {
        let chunks = [];
        let last = 0;
        // Only look at matches corresponding to event (ei) and field (fi)
        for (const m of this.state.matches.filter(m => m.eventIdx === ei && m.fieldIdx === f.idx)) {
            chunks.push(<span>{f.value.substring(last, m.start)}</span>);
            chunks.push(<span
                id={"timeline-match-" + String(m.matchIdx)}
                className={"timeline-highlight" + (m.matchIdx === this.state.matchIdx ? " timeline-highlight-active" : "")}
            >{f.value.substring(m.start, m.end)}</span>);
            last = m.end;
        }
        chunks.push(<span>{f?.value.substring(last)}</span>);
        return chunks.map((c,i) => <React.Fragment key={i}>{c}</React.Fragment>);
    }

    render = () => {
        return (<>
            {/* Navigation Bar */}
            <Navigation
                user={this.props.user}
                team={this.props.team}
            />

            {/* User & Notifications */}
            <User
                user={this.props.user}
                team={this.props.team}
                updateUser={this.props.updateUser}
                switchTeam={this.props.switchTeam}
                clearNotification={this.props.clearNotification}
            />

            {/* Timeline search */}
            <Search
                user={this.props.user}
                query={this.state.query}
                search={this.search}
                searching={this.state.searching}
                placeholder="Search timeline..."
            />

            {/* Search results/matches counter */}
            {this.state.matches?.length ? <MatchCounter
                className="timeline-match-counter"
                idx={this.state.matchIdx}
                count={this.state.matches?.length ?? 0}
                goto={this.gotoMatch}
            /> : ""}

            {/* Number of results in documents */}
            {this.state.numDocResults ? <div
                className="timeline-doc-results"
                onClick={() => window.open('/stack/' + this.props.match.params.stackId + '/search/' + encodeURIComponent(this.state.query), '_blank')}
            >
                See {this.state.numDocResults} results in documents
            </div> : ""}

            {/* Back */}
            <div
                className="title-back"
                onClick={() => {
                    window.location.href = "/stack/" + this.props.match.params.stackId
                }}
            >Back</div>

            <div className="title-bar">
                {/* Title */}
                <div className="timeline-title">
                    <div className="title-text">{this.state.name ? this.state.name + " - Medical History" : "Loading..."}</div>
                </div>

                {/* Timeline Sort */}
                <div className="timeline-doc-sort"
                    onClick={() => {
                        const sortDirection = this.state.sortDir * -1;
                        this.setState({sortDir: sortDirection});
                        this.sortTimeline(sortDirection);
                    }}
                >{this.state.sortDir > 0? "Newest to oldest": "Oldest to newest"}</div>
            </div>

            {/* Timeline */}
            <div className="main-content full timeline-container">
                {this.state.events?.length ? 
                <Scrollbars
                    renderTrackVertical={props => <div className="scrollbar-track-v" {...props} />}
                    renderTrackHorizontal={props => <div className="scrollbar-track-h" {...props} />}
                    renderThumbVertical={props => <div className="scrollbar-thumb-v" {...props} />}
                    renderThumbHorizontal={props => <div className="scrollbar-thumb-h" {...props} />}
                >
                    <div id="timeline-container" className="timeline">
                        {this.state.events.map((e, eventIdx) => {
                            const m = e.metadata;
                            const summary = getField(m, 'summary');
                            const meds = getFields(m, 'medication');
                            const mc = meds.reduce((a, m) => Math.min(a, m.confidence), 1.0);
                            const cpt = getFields(m, 'cpt');
                            const icd = getFields(m, 'icd');

                            // Highlight with event filter
                            const h = (f) => this.highlight(eventIdx, f);

                            return (<div
                                className="timeline-event"
                                id={"timeline-event-" + String(eventIdx)}
                                key={eventIdx}
                                onClick={() => window.open('/stack/' + this.props.match.params.stackId + '/document/' + e.document, '_blank')}
                            >
                                <div className="timeline-event-icon" />

                                <div className="timeline-event-date">{e.date?.toLocaleDateString('en-us', { year: 'numeric', month: 'short', day: 'numeric' })}</div>
                                <div className="timeline-event-weekday">{weekdays[e.date?.getDay()]}</div>

                                <div className="timeline-event-details">
                                    {
                                        getField(m, 'report type').value === getField(m, 'title').value ?
                                        <h1>{h(getField(m, 'report type'))}</h1> :
                                        <h1>{h(getField(m, 'report type'))} - {h(getField(m, 'title'))}</h1>

                                    }
                                    <p>{this.alert(summary)}{h(summary)}</p>

                                    {icd?.length ? <>
                                        <p>
                                            <strong>Diagnoses:</strong>
                                        </p>
                                        <ul>
                                            {icd.map((c, idx) => <li key={idx}>
                                                {this.alert(c)}
                                                {h(c)}
                                            </li>)}
                                        </ul>
                                    </> : ""}

                                    {cpt?.length ? <>
                                        <p>
                                            <strong>Treatments:</strong>
                                        </p>
                                        <ul>
                                            {cpt.map((c, idx) => <li key={idx}>
                                                {this.alert(c)}
                                                {h(c)}
                                            </li>)}
                                        </ul>
                                    </> : ""}

                                    {meds?.length ? <p>
                                        {this.alert({ confidence: mc })}
                                        <strong>Medications: </strong>
                                        {meds.map((c, idx) => <span key={idx}>
                                            {idx > 0 ? ', ' : ''}
                                            {h(c)}
                                        </span>)}
                                    </p> : ""}
                                </div>

                                {['physician', 'facility'].map((k, idx) => {
                                    const f = getField(m, k);
                                    return (<div
                                        className={"timeline-event-" + k}
                                        key={idx}
                                    >
                                        {this.alert(f)}
                                        {h(f)}
                                    </div>);
                                })}
                            </div>);
                        })}
                    </div>
                </Scrollbars> : <div className="timeline-empty">
                    This stack doesn't contain any documents with medical events.
                </div>}
            </div>
            <div className="timeline-years">
                {this.state.years.map(this.year)}
            </div>
            <div className="timeline-matches">
                {this.state.matches.map(this.match)}
            </div>
        </>);
    }

}